// src/lib/authCleanup.js
//
// Hardening Sprint Track 1.1 — single chokepoint for nuking user-specific
// state on logout / account switch. Before this existed each call site
// cleared a different subset (cache here, localStorage there, realtime
// channels left dangling) and User A's data routinely surfaced under
// User B's session. This is the only thing that should ever be called
// to flush a user's state.
//
// Call sites:
//   - Explicit logout buttons (Dashboard, PlayerView, Profile) BEFORE
//     supabase.auth.signOut()
//   - AuthContext onAuthStateChange → SIGNED_OUT
//   - AuthContext onAuthStateChange → SIGNED_IN with a different user id
//     than the previous session (account switch on the same tab)
//
// What gets cleared:
//   1. All dc_-prefixed localStorage entries EXCEPT per-user display-mode
//      keys (dc_display_mode_<uuid>) — those are intentionally scoped per
//      user so re-login restores the leaver's preference instead of
//      losing it. The active-user pointer + global legacy theme key are
//      cleared (clearActiveOnLogout handles the pointer + DOM revert).
//   2. dc_join_token, pending_join_code, and any other ad-hoc app keys.
//   3. The dc_ sessionStorage namespace (not currently used heavily but
//      future-proofs against drift).
//   4. The IndexedDB SWR cache (db.clearCacheOnSignOut → cache.clearAll
//      + outbox.clearAll). Outbox clearance is deliberate even though it
//      drops unsent work — preventing cross-account leakage outweighs
//      preserving an offline queue that belongs to the leaving user.
//   5. Every active Supabase realtime channel
//      (supabase.removeAllChannels). Channel cleanup is otherwise tied
//      to React component unmount, which doesn't run reliably during
//      a fast auth flip.
//   6. Module-level state in displayMode (clearActiveOnLogout reverts
//      the <html data-dc-mode> attribute back to dark).
//
// What we do NOT clear:
//   - Supabase auth storage (sb-*-auth-token). That's signOut()'s job —
//     it has to negotiate the refresh-token revoke. Nuking it here would
//     race with signOut.

import { supabase } from './supabaseClient'
import cache from './localCache'
import outbox from './outbox'
import { clearActiveOnLogout } from './displayMode'
import realtimeManager from './realtimeManager'

const APP_PREFIX = 'dc_'
const PRESERVE_PREFIX = 'dc_display_mode_'   // per-user theme keyed by uuid
const ONE_OFF_KEYS = ['pending_join_code', 'dc_join_token']

function clearScopedStorage(storage) {
  if (!storage) return
  // Snapshot keys first; removeItem during iteration mutates the index.
  const keys = []
  try {
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i)
      if (!k) continue
      if (ONE_OFF_KEYS.includes(k)) { keys.push(k); continue }
      if (!k.startsWith(APP_PREFIX)) continue
      // Keep dc_display_mode_<userId> entries — they're per-user
      // preferences, not session state, and the active-user pointer
      // (which IS cleared via clearActiveOnLogout) gates whether
      // they're applied.
      if (k.startsWith(PRESERVE_PREFIX)) continue
      keys.push(k)
    }
    keys.forEach(k => { try { storage.removeItem(k) } catch { /* ignore */ } })
  } catch { /* SSR / disabled storage — nothing to clean */ }
}

export async function clearAllUserState() {
  // 1 + 2 + 3. Storage namespaces.
  if (typeof window !== 'undefined') {
    clearScopedStorage(window.localStorage)
    clearScopedStorage(window.sessionStorage)
  }

  // 6. displayMode module state. Drops the active-user pointer and
  // reverts the DOM to dark so the next signer-in on this browser
  // doesn't flash the leaver's mode before AuthContext rehydrates.
  try { clearActiveOnLogout() } catch { /* non-blocking */ }

  // 4. IndexedDB SWR cache + offline outbox.
  try { await cache.clearAll() } catch { /* non-blocking */ }
  try { await outbox.clearAll() } catch { /* non-blocking */ }

  // 5. Realtime channels. The Track 1.3 manager owns every managed
  // subscription via its registry — clear that first so the manager's
  // internal map matches reality. Then ALSO call removeAllChannels()
  // as a belt-and-suspenders for any pre-migration channel that's
  // still being created directly by a component (those land on the
  // shared supabase client even though the manager doesn't track them).
  try { realtimeManager.clearAll() } catch { /* non-blocking */ }
  try { await supabase.removeAllChannels() } catch { /* non-blocking */ }
}

export default clearAllUserState
